import { CommonModule } from '@angular/common';
import { Component, input, output, ChangeDetectionStrategy, signal, computed, effect } from '@angular/core';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface FormFooterStepConfig {
  label: string;
  isValid: () => boolean;
}

export type SubmitState = 'enabled' | 'submitting' | 'disabled';

@Component({
  selector: 'form-footer',
  templateUrl: './form-footer.component.html',
  styleUrls: ['./form-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatStepperModule, MatTooltipModule]
})
export class FormFooterComponent {
  stepConfigs = input.required<FormFooterStepConfig[]>();
  submitState = input<SubmitState>('enabled');
  submitTooltip = input<string>('');

  stepChange = output<number>();
  cancelClicked = output<void>();
  submitClicked = output<void>();

  activeStep = signal(0);
  visitedSteps = signal<boolean[]>([]);

  showCancel = computed(() => this.activeStep() === 0);
  showBack = computed(() => this.activeStep() > 0);
  showNext = computed(() => {
    const configs = this.stepConfigs();
    const active = this.activeStep();
    return active < configs.length - 1;
  });
  showSubmit = computed(() => {
    const configs = this.stepConfigs();
    const active = this.activeStep();
    return active === configs.length - 1;
  });

  steps = computed(() => {
    const configs = this.stepConfigs();
    const active = this.activeStep();
    const visited = this.visitedSteps();

    return configs.map((config, index) => ({
      stepNumber: index + 1,
      label: config.label,
      invalid: (active === index || visited[index]) && !config.isValid(),
      done: active > index
    }));
  });

  constructor() {
    effect(() => {
      const configs = this.stepConfigs();
      const visited = this.visitedSteps();

      if (visited.length !== configs.length)  this.visitedSteps.set(configs.map((_, i) => visited[i] ?? i === 0));
    }, { allowSignalWrites: true });
  }

  isSubmitting = computed(() => this.submitState() === 'submitting');
  submitDisabled = computed(() => this.submitState() === 'disabled' || this.submitState() === 'submitting');
  showSubmitTooltip = computed(() => this.submitState() === 'disabled' && !!this.submitTooltip());

  canNavigateToStep(stepIndex: number): boolean {
    const visited = this.visitedSteps();
    if (this.isSubmitting()) return this.activeStep() === stepIndex;
    return this.activeStep() === stepIndex || visited[stepIndex];
  }

  onStepClick(event: Event, stepIndex: number): void {
    if (!this.canNavigateToStep(stepIndex)) return;

    event.preventDefault();
    this.activeStep.set(stepIndex);
    this.markStepVisited(stepIndex);
    this.stepChange.emit(stepIndex);
  }

  markStepVisited(stepIndex: number): void {
    const current = this.visitedSteps();
    const next = [...current];
    next[stepIndex] = true;
    this.visitedSteps.set(next);
  }

  goToStep(stepIndex: number): void {
    this.activeStep.set(stepIndex);
    this.markStepVisited(stepIndex);
  }
}
